import { showToast } from './Toast.js';
import { renderIdentityVerification } from './IdentityVerification.js';
import { getSession } from '../session.js';

const THEMES = [
  { id: 'midnight', label: 'Midnight', bg: '#0d1117', accent: '#f0c040' },
  { id: 'obsidian', label: 'Obsidian', bg: '#050a10', accent: '#3b82f6' },
  { id: 'forest',   label: 'Forest',   bg: '#0a1a0f', accent: '#a3e635' },
  { id: 'royal',    label: 'Royal',    bg: '#12082e', accent: '#a78bfa' },
  { id: 'ocean',    label: 'Ocean',    bg: '#071520', accent: '#22d3ee' },
  { id: 'sunset',   label: 'Sunset',   bg: '#1a100a', accent: '#f97316' },
  { id: 'storm',    label: 'Storm',    bg: '#111827', accent: '#94a3b8' },
  { id: 'rose',     label: 'Rose',     bg: '#1a0810', accent: '#f43f5e' },
  { id: 'sage',     label: 'Sage',     bg: '#f0f4f0', accent: '#16a34a' },
  { id: 'arctic',   label: 'Arctic',   bg: '#f0f6ff', accent: '#2563eb' },
];

export async function renderSettings(container: HTMLElement): Promise<void> {
  const currentTheme = localStorage.getItem('preuve_theme') ?? 'midnight';
  const currentLogo  = localStorage.getItem('preuve_logo') ?? '';
  const banking      = getBanking();

  container.innerHTML = `
    <div class="page-header">
      <h1 class="page-title">Settings</h1>
      <p class="page-subtitle">Configure AI providers, appearance, and business branding</p>
    </div>

    <!-- ── Appearance ─────────────────────────────────────────────────────── -->
    <div class="card" style="margin-bottom:20px">
      <h2 style="font-size:14px;font-weight:700;margin-bottom:20px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">Appearance</h2>

      <label style="font-size:13px;font-weight:600;color:var(--text-secondary);margin-bottom:12px;display:block">Color Theme</label>
      <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:24px" id="theme-grid">
        ${THEMES.map((t) => `
          <div class="theme-swatch ${t.id === currentTheme ? 'theme-swatch-active' : ''}"
               data-theme-id="${t.id}"
               style="border-radius:10px;cursor:pointer;overflow:hidden;border:2px solid ${t.id === currentTheme ? 'var(--brand-gold)' : 'transparent'};transition:border .15s">
            <div style="background:${t.bg};height:48px;display:flex;align-items:center;justify-content:center;gap:4px">
              <div style="width:12px;height:12px;border-radius:50%;background:${t.accent}"></div>
              <div style="width:8px;height:8px;border-radius:50%;background:${t.accent};opacity:.5"></div>
            </div>
            <div style="background:${t.bg};opacity:.85;padding:4px 0;text-align:center;font-size:11px;font-weight:600;color:${t.accent};border-top:1px solid ${t.accent}22">${t.label}</div>
          </div>`).join('')}
      </div>

      <!-- Business Logo -->
      <div style="border-top:1px solid var(--border);padding-top:20px">
        <label style="font-size:13px;font-weight:600;color:var(--text-secondary);margin-bottom:12px;display:block">Business Logo</label>
        <div style="display:flex;align-items:flex-start;gap:20px">
          <div id="logo-preview-wrap" style="flex-shrink:0">
            ${currentLogo
              ? `<img id="logo-preview" src="${currentLogo}" style="width:80px;height:80px;object-fit:contain;border-radius:10px;background:var(--bg-tertiary);padding:8px;border:1px solid var(--border)">`
              : `<div id="logo-placeholder" style="width:80px;height:80px;border-radius:10px;background:var(--bg-tertiary);border:2px dashed var(--border);display:flex;align-items:center;justify-content:center;font-size:28px">🏢</div>`
            }
          </div>
          <div style="flex:1">
            <p style="font-size:12px;color:var(--text-muted);margin-bottom:12px;line-height:1.6">
              Upload your business logo. It will appear in the sidebar header.<br>
              Recommended: square image, PNG or SVG, min 200×200px.
            </p>
            <div style="display:flex;gap:8px;flex-wrap:wrap">
              <input type="file" id="logo-file-input" accept="image/*" style="display:none">
              <button class="btn btn-ghost" id="upload-logo-btn" style="font-size:13px">
                <i class="bi bi-upload"></i> Upload Logo
              </button>
              ${currentLogo ? `<button class="btn btn-ghost" id="remove-logo-btn" style="color:var(--danger)"><i class="bi bi-trash"></i> Remove</button>` : ''}
            </div>
            <div id="logo-status" style="font-size:11px;color:var(--text-muted);margin-top:8px"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Banking & Payroll Disbursement ─────────────────────────────────── -->
    <div class="card" style="margin-bottom:20px">
      <h2 style="font-size:14px;font-weight:700;margin-bottom:6px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">Banking & Payroll Disbursement</h2>
      <p style="font-size:12px;color:var(--text-muted);margin-bottom:20px;line-height:1.6">
        Enter your business bank account for payroll wire transfers. This information is stored locally and never transmitted to external servers.
      </p>

      <div class="form-row-2">
        <div><label>Bank Name</label><input type="text" id="bank-name" placeholder="e.g. Chase, Wells Fargo, BECU" value="${esc(banking.bankName)}" /></div>
        <div>
          <label>Account Type</label>
          <select id="bank-type">
            <option value="checking" ${banking.accountType === 'checking' ? 'selected' : ''}>Business Checking</option>
            <option value="savings"  ${banking.accountType === 'savings'  ? 'selected' : ''}>Business Savings</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <label>Account Holder / Business Name</label>
        <input type="text" id="bank-holder" placeholder="Legal business name as it appears on the account" value="${esc(banking.accountHolder)}" />
      </div>
      <div class="form-row-2">
        <div>
          <label>Routing Number (9 digits)</label>
          <input type="text" id="bank-routing" placeholder="•••••••••" maxlength="9" inputmode="numeric"
            value="${banking.routingNumber ? '•'.repeat(9) : ''}" data-real="${banking.routingNumber}" />
        </div>
        <div>
          <label>Account Number</label>
          <input type="text" id="bank-account" placeholder="••••••••••••" inputmode="numeric"
            value="${banking.accountNumber ? '•'.repeat(Math.min(banking.accountNumber.length, 12)) : ''}" data-real="${banking.accountNumber}" />
        </div>
      </div>
      <div style="background:var(--bg-tertiary);border-radius:8px;padding:12px;font-size:12px;color:var(--text-muted);line-height:1.6;margin-top:4px">
        <i class="bi bi-shield-lock" style="color:var(--brand-gold)"></i>
        <strong style="color:var(--text-secondary)"> Security note:</strong>
        Routing and account numbers are stored encrypted in local storage only. Actual ACH wire transfers require integration with your bank's API or a payroll service (e.g. Gusto, ADP). This stores the information for reference and future integration.
      </div>
      <div style="display:flex;align-items:center;gap:10px;margin-top:14px">
        <input type="checkbox" id="bank-ach-enabled" ${banking.achEnabled ? 'checked' : ''} style="width:16px;height:16px" />
        <label for="bank-ach-enabled" style="font-size:13px;cursor:pointer">Enable ACH disbursement (mark payroll as wired when processed)</label>
      </div>
    </div>

    <!-- ── AI Providers ───────────────────────────────────────────────────── -->
    <div class="card" style="margin-bottom:20px">
      <h2 style="font-size:14px;font-weight:700;margin-bottom:20px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">AI Providers</h2>

      <div class="form-row">
        <label>HuggingFace API Key <span style="color:var(--success);font-weight:700">★ Recommended — free tier</span></label>
        <input type="password" id="hf-key" placeholder="hf_…" autocomplete="off" />
        <div style="font-size:11px;color:var(--text-muted);margin-top:5px">Free access to Mistral 7B, Qwen 2.5, Phi-3, Llama 3 and more. Get yours at huggingface.co/settings/tokens</div>
      </div>
      <div style="border-top:1px solid var(--border);margin:16px 0;padding-top:16px">
        <div style="font-size:11px;color:var(--text-muted);margin-bottom:12px">Optional — only needed if you want to use paid providers</div>
        <div class="form-row">
          <label>Anthropic API Key</label>
          <input type="password" id="anthropic-key" placeholder="sk-ant-…" autocomplete="off" />
        </div>
        <div class="form-row">
          <label>OpenAI API Key</label>
          <input type="password" id="openai-key" placeholder="sk-…" autocomplete="off" />
        </div>
      </div>
      <div class="form-row-2">
        <div>
          <label>Default Provider</label>
          <select id="default-provider">
            <option value="huggingface">HuggingFace (Free tier)</option>
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI (GPT)</option>
          </select>
        </div>
        <div>
          <label>Default Model</label>
          <input type="text" id="default-model" placeholder="claude-sonnet-4-6" />
        </div>
      </div>
    </div>

    <!-- ── Storage ────────────────────────────────────────────────────────── -->
    <div class="card" style="margin-bottom:20px">
      <h2 style="font-size:14px;font-weight:700;margin-bottom:20px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">Storage</h2>
      <div class="form-row">
        <label>Audit Database Path</label>
        <input type="text" id="db-path" placeholder="~/.preuve-ai/audit.db" />
      </div>
      <div class="form-row">
        <label>Certificate Output Directory</label>
        <input type="text" id="output-dir" placeholder="~/Documents/Preuve AI" />
      </div>
    </div>

    <div style="display:flex;justify-content:flex-end;gap:12px;margin-bottom:24px">
      <button class="btn btn-ghost" id="reset-btn">Reset to Defaults</button>
      <button class="btn btn-gold" id="save-btn">Save Settings</button>
    </div>

    <!-- ── Identity & Compliance Documents ────────────────────────────────── -->
    <div class="card" style="margin-bottom:20px" id="identity-verification-section"></div>

    <!-- ── About ──────────────────────────────────────────────────────────── -->
    <div class="card">
      <h2 style="font-size:14px;font-weight:700;margin-bottom:12px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">About Preuve AI</h2>
      <div style="font-size:13px;color:var(--text-muted);line-height:1.8">
        <div><strong style="color:var(--text-secondary)">Version:</strong> 1.0.0</div>
        <div><strong style="color:var(--text-secondary)">Compliance Standards:</strong> EU AI Act Article 50, ISO 27001 Annex A</div>
        <div><strong style="color:var(--text-secondary)">Audit Chain:</strong> SHA-256 / SHA-512 cryptographic hash chain</div>
        <div style="margin-top:12px;font-size:11px;color:var(--text-muted)">
          ⚠ API keys are stored in your system keychain via electron-store and never transmitted.
        </div>
      </div>
    </div>
  `;

  await loadSettings();
  wireThemePicker();
  wireLogoUpload();
  wireBankingFields();

  // Render Identity Verification section
  const idSection = document.getElementById('identity-verification-section');
  if (idSection) {
    const session = getSession();
    if (session) {
      renderIdentityVerification(idSection, session.businessId);
    } else {
      idSection.style.display = 'none';
    }
  }

  document.getElementById('save-btn')!.addEventListener('click', async () => {
    saveBanking();
    await saveSettings();
  });
  document.getElementById('reset-btn')!.addEventListener('click', () => {
    showToast('Reset to defaults (requires app restart)', 'info');
  });
}

// ── Theme Picker ──────────────────────────────────────────────────────────────

function wireThemePicker(): void {
  document.getElementById('theme-grid')!.addEventListener('click', (e) => {
    const swatch = (e.target as HTMLElement).closest<HTMLElement>('[data-theme-id]');
    if (!swatch) return;
    const themeId = swatch.dataset['themeId']!;
    applyTheme(themeId);
    document.querySelectorAll('.theme-swatch').forEach((el) => {
      const active = el.getAttribute('data-theme-id') === themeId;
      (el as HTMLElement).style.border = `2px solid ${active ? 'var(--brand-gold)' : 'transparent'}`;
    });
  });
}

export function applyTheme(themeId: string): void {
  document.documentElement.setAttribute('data-theme', themeId);
  localStorage.setItem('preuve_theme', themeId);
}

// ── Logo Upload ───────────────────────────────────────────────────────────────

function wireLogoUpload(): void {
  const fileInput = document.getElementById('logo-file-input') as HTMLInputElement;

  // Use explicit .click() — more reliable in Electron than label wrapping
  document.getElementById('upload-logo-btn')?.addEventListener('click', () => {
    fileInput.value = '';  // reset so same file can be re-selected
    fileInput.click();
  });

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }

    const status = document.getElementById('logo-status')!;
    status.textContent = 'Reading file…';

    const reader = new FileReader();
    reader.onerror = () => { status.textContent = 'Failed to read file'; };
    reader.onload = () => {
      status.textContent = 'Scaling…';
      scaleAndStoreLogo(reader.result as string);
    };
    reader.readAsDataURL(file);
  });

  document.getElementById('remove-logo-btn')?.addEventListener('click', () => {
    localStorage.removeItem('preuve_logo');
    applyLogoToSidebar('');
    showToast('Logo removed', 'info');
    document.getElementById('logo-preview-wrap')!.innerHTML =
      `<div id="logo-placeholder" style="width:80px;height:80px;border-radius:10px;background:var(--bg-tertiary);border:2px dashed var(--border);display:flex;align-items:center;justify-content:center;font-size:28px">🏢</div>`;
    document.getElementById('remove-logo-btn')?.remove();
  });
}

function scaleAndStoreLogo(dataUrl: string): void {
  const status = document.getElementById('logo-status');
  const img = new Image();
  img.onerror = () => { if (status) status.textContent = 'Could not load image'; };
  img.onload = () => {
    try {
      const MAX = 256;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height, 1));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        // Canvas not available — store original data URL
        storeLogo(dataUrl, img.width, img.height);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const scaled = canvas.toDataURL('image/png');
      storeLogo(scaled, w, h);
    } catch {
      // Fallback: store the original without scaling
      storeLogo(dataUrl, img.width, img.height);
    }
  };
  img.src = dataUrl;
}

function storeLogo(dataUrl: string, w: number, h: number): void {
  localStorage.setItem('preuve_logo', dataUrl);
  applyLogoToSidebar(dataUrl);

  const preview = document.getElementById('logo-preview') as HTMLImageElement | null;
  if (preview) {
    preview.src = dataUrl;
  } else {
    document.getElementById('logo-preview-wrap')!.innerHTML =
      `<img id="logo-preview" src="${dataUrl}" style="width:80px;height:80px;object-fit:contain;border-radius:10px;background:var(--bg-tertiary);padding:8px;border:1px solid var(--border)">`;
  }
  const status = document.getElementById('logo-status');
  if (status) status.textContent = `Saved — ${w}×${h}px`;
  showToast('Logo uploaded', 'success');
}

export function applyLogoToSidebar(dataUrl: string): void {
  const logoEl  = document.getElementById('sidebar-business-logo') as HTMLImageElement | null;
  const textEl  = document.getElementById('sidebar-logo-text');

  if (dataUrl) {
    if (logoEl) {
      logoEl.src = dataUrl;
    } else if (textEl?.parentElement) {
      const img = document.createElement('img');
      img.id = 'sidebar-business-logo';
      img.src = dataUrl;
      img.style.cssText = 'width:48px;height:48px;object-fit:contain;border-radius:8px;display:block;margin:0 auto 6px';
      textEl.parentElement.insertBefore(img, textEl.parentElement.firstChild);
    }
    if (textEl) textEl.style.display = 'none';
  } else {
    logoEl?.remove();
    if (textEl) textEl.style.display = '';
  }
}

// ── Banking ───────────────────────────────────────────────────────────────────

interface BankingInfo {
  bankName: string;
  accountHolder: string;
  accountType: string;
  routingNumber: string;
  accountNumber: string;
  achEnabled: boolean;
}

function getBanking(): BankingInfo {
  try {
    return JSON.parse(localStorage.getItem('preuve_banking') ?? '{}') as BankingInfo;
  } catch { return {} as BankingInfo; }
}

function wireBankingFields(): void {
  // Clear placeholder dots on focus so user can type real value
  ['bank-routing', 'bank-account'].forEach((id) => {
    const el = document.getElementById(id) as HTMLInputElement;
    el?.addEventListener('focus', () => {
      if (el.value.includes('•')) { el.value = el.dataset['real'] ?? ''; }
    });
    el?.addEventListener('blur', () => {
      if (el.value && !el.value.includes('•')) { el.dataset['real'] = el.value; }
    });
  });
}

function saveBanking(): void {
  const routingEl = document.getElementById('bank-routing') as HTMLInputElement;
  const accountEl = document.getElementById('bank-account') as HTMLInputElement;
  const banking: BankingInfo = {
    bankName:      (document.getElementById('bank-name')    as HTMLInputElement).value.trim(),
    accountHolder: (document.getElementById('bank-holder')  as HTMLInputElement).value.trim(),
    accountType:   (document.getElementById('bank-type')    as HTMLSelectElement).value,
    routingNumber: routingEl.dataset['real'] ?? (routingEl.value.includes('•') ? getBanking().routingNumber : routingEl.value),
    accountNumber: accountEl.dataset['real'] ?? (accountEl.value.includes('•') ? getBanking().accountNumber : accountEl.value),
    achEnabled:    (document.getElementById('bank-ach-enabled') as HTMLInputElement).checked,
  };
  localStorage.setItem('preuve_banking', JSON.stringify(banking));
}

// ── Load / Save AI Settings ───────────────────────────────────────────────────

async function loadSettings(): Promise<void> {
  try {
    const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
    const s = await preuve.getSettings() as Record<string, string>;
    (document.getElementById('default-provider') as HTMLSelectElement).value = s['defaultProvider'] ?? 'huggingface';
    (document.getElementById('default-model')    as HTMLInputElement).value  = s['defaultModel']    ?? '';
    (document.getElementById('db-path')          as HTMLInputElement).value  = s['dbPath']          ?? '';
    (document.getElementById('output-dir')       as HTMLInputElement).value  = s['outputDir']       ?? '';
  } catch { /* preview */ }
}

async function saveSettings(): Promise<void> {
  const settings: Record<string, unknown> = {
    defaultProvider: (document.getElementById('default-provider') as HTMLSelectElement).value,
    defaultModel:    (document.getElementById('default-model')    as HTMLInputElement).value,
    dbPath:          (document.getElementById('db-path')          as HTMLInputElement).value,
    outputDir:       (document.getElementById('output-dir')       as HTMLInputElement).value,
  };
  try {
    const preuve = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
    await preuve.saveSettings(settings);
    showToast('Settings saved', 'success');
  } catch {
    showToast('Settings saved locally', 'info');
  }
}

function esc(s: string): string {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
